import {
  RecordingCancelledError,
  StreamService,
} from './audio/streamService.ts'
import {
  EdenaiApi,
  throwIfSpeechCancelled,
} from '../../shared/api/edenaiApi.ts'
import { Howl } from 'howler'
import { ErrorNotifier } from '../../shared/api/httpClient.ts'
import {
  AUDIO_LOAD_ERROR,
  AUDIO_PLAY_ERROR,
  howlerPlay,
} from '../../shared/lib/howlerUtils.ts'
import { clearTextForSpeech } from '../../shared/lib/utils.ts'
import { TeacherAudioRenderer } from './audio/teacherAudioRenderer.ts'

const TEACHER_AUDIO_UNLOCK_EVENTS = [
  'pointerdown',
  'touchend',
  'click',
  'keydown',
] as const

export function isSpeechDownloadUrl(src: string) {
  return /^(?:https?:\/\/[^/]+)?\/api\/text_to_speech\/download\//.test(src)
}

export class SpeechService {
  audio?: HTMLAudioElement
  inited = false
  private owners = new Set<symbol>()
  private teacherAudioOwners = new Set<symbol>()
  private playRequest = 0
  private teacherContext?: AudioContext
  private teacherUnlockSource?: AudioBufferSourceNode
  private currentTeacher?: {
    messageId: number
    controller: AbortController
    paused: boolean
  }

  private getTeacherContext() {
    this.streamService.usePlaybackSession()
    if (this.teacherContext?.state === 'closed') this.teacherContext = undefined
    if (!this.teacherContext) {
      const context = new AudioContext()
      this.teacherContext = context
    }
    return this.teacherContext
  }

  private unlockTeacherAudio = () => {
    // Unlock while a user gesture is active, before an automatic chat response.
    try {
      if (
        this.streamService.isCapturing ||
        this.currentTeacher?.paused === true ||
        this.teacherContext?.state === 'running'
      )
        return
      const context = this.getTeacherContext()
      void context.resume().catch(() => {
        // Playback reports blocked audio if a later request still cannot resume.
      })
      // Starting a silent source in the gesture also unlocks iOS Web Audio.
      this.clearTeacherUnlockSource()
      const source = context.createBufferSource()
      source.buffer = context.createBuffer(1, 1, context.sampleRate)
      source.connect(context.destination)
      source.onended = () => {
        source.disconnect()
        if (this.teacherUnlockSource === source)
          this.teacherUnlockSource = undefined
      }
      this.teacherUnlockSource = source
      source.start(0)
    } catch {
      // playTeacherText reports unsupported/blocked audio when it is requested.
    }
  }

  constructor(
    public streamService: StreamService,
    private edenaiApi: EdenaiApi,
    private errorNotifier: ErrorNotifier,
  ) {}

  private listenForTeacherAudio() {
    for (const event of TEACHER_AUDIO_UNLOCK_EVENTS)
      window.addEventListener(event, this.unlockTeacherAudio, true)
  }

  // The app owns the unlock listener before a navigation gesture opens a chat.
  acquireTeacherAudio() {
    const owner = Symbol()
    this.teacherAudioOwners.add(owner)
    this.listenForTeacherAudio()
    return () => {
      if (this.teacherAudioOwners.delete(owner)) this.releaseTeacherAudio()
    }
  }

  private clearTeacherUnlockSource() {
    const source = this.teacherUnlockSource
    this.teacherUnlockSource = undefined
    if (source) {
      source.onended = null
      try {
        source.stop()
      } catch {
        // The one-shot unlock source may already have ended.
      }
      source.disconnect()
    }
  }

  private releaseTeacherAudio() {
    if (this.inited || this.teacherAudioOwners.size > 0) return
    for (const event of TEACHER_AUDIO_UNLOCK_EVENTS)
      window.removeEventListener(event, this.unlockTeacherAudio, true)
    if (this.teacherContext) this.discardTeacherContext(this.teacherContext)
  }

  private discardTeacherContext(context: AudioContext) {
    if (this.teacherContext === context) {
      this.clearTeacherUnlockSource()
      this.teacherContext = undefined
    }
    // A broken native close() can hang too; it must not block a fresh gesture.
    void context.close().catch(() => {
      // The old context is already detached; a close failure must not block playback.
    })
  }

  private replaceTeacherContext(context: AudioContext, signal: AbortSignal) {
    throwIfSpeechCancelled(signal)
    if (this.teacherContext !== context)
      throw new DOMException('Playback superseded', 'AbortError')
    this.discardTeacherContext(context)
    return this.getTeacherContext()
  }

  private discardFailedRenderer(renderer: TeacherAudioRenderer) {
    if (
      renderer.needsFreshContext &&
      renderer.context &&
      this.teacherContext === renderer.context
    )
      this.discardTeacherContext(renderer.context)
  }

  private resumeTeacherAudio(context: AudioContext, signal: AbortSignal) {
    return new Promise<void>((resolve, reject) => {
      let settled = false
      const finish = (error?: Error) => {
        if (settled) return
        settled = true
        clearTimeout(timeout)
        signal.removeEventListener('abort', cancel)
        context.removeEventListener('statechange', onStateChange)
        if (error) reject(error)
        else resolve()
      }
      const onStateChange = () => {
        if (context.state === 'running') finish()
      }
      const cancel = () => {
        finish(new DOMException('Playback cancelled', 'AbortError'))
      }
      const timeout = setTimeout(() => {
        finish(
          Object.assign(new Error(AUDIO_PLAY_ERROR), {
            cause: new Error(
              'AudioContext did not reach running within 1500 ms',
            ),
          }),
        )
      }, 1500)
      signal.addEventListener('abort', cancel, { once: true })
      context.addEventListener('statechange', onStateChange)
      if (signal.aborted) {
        cancel()
        return
      }
      if (context.state === 'running') {
        finish()
        return
      }
      void context.resume().then(
        () => {
          finish(
            context.state === 'running'
              ? undefined
              : Object.assign(new Error(AUDIO_PLAY_ERROR), {
                  cause: new Error(
                    `AudioContext.resume() resolved with state ${context.state}`,
                  ),
                }),
          )
        },
        (error: unknown) => {
          finish(Object.assign(new Error(AUDIO_PLAY_ERROR), { cause: error }))
        },
      )
    })
  }

  init() {
    this.inited = true
    this.listenForTeacherAudio()
  }

  acquire() {
    const owner = Symbol()
    this.owners.add(owner)
    this.init()
    return () => {
      if (this.owners.delete(owner) && this.owners.size === 0) {
        this.dispose()
      }
    }
  }

  async start(recordingStatus: { recording: boolean }) {
    const shouldRecord = () => this.inited && recordingStatus.recording
    if (!shouldRecord()) return
    try {
      const recorder = await this.streamService.getRecorder()
      if (this.streamService.items?.recorder !== recorder) return
      if (shouldRecord()) {
        recorder.start()
      } else {
        this.streamService.stopTracks()
      }
    } catch (error) {
      if (error instanceof RecordingCancelledError) return
      this.streamService.stopTracks()
      throw error
    }
  }

  async requestData() {
    // Automatic speech-end polling must never acquire a microphone during TTS.
    return await this.streamService.items?.recorder.requestData()
  }

  stopRecording() {
    return this.streamService.stopRecording()
  }
  async stopAndAnalyze(chatId: number) {
    const blob = await this.stopRecording()
    if (!blob) {
      return ''
    }
    const result = await this.edenaiApi.speechToText(chatId, blob)
    return result.text == '.' ? '' : result.text
  }

  dispose() {
    this.inited = false
    this.streamService.stopTracks()
    this.audio = undefined
    this.stopCurrentSound()
    this.releaseTeacherAudio()
  }

  async playTeacherText(
    messageId: number | undefined,
    chatId: number | undefined,
    text: string,
    progressCallback: (progress: number) => void,
    startCallback?: () => void,
    endCallback?: () => void,
  ) {
    if (!this.inited) return
    if (messageId === undefined || chatId === undefined) {
      return this.playText(
        messageId,
        chatId,
        text,
        progressCallback,
        startCallback,
        endCallback,
      )
    }
    this.stopCurrentSound()
    const controller = new AbortController()
    const current = { messageId, controller, paused: false }
    this.currentTeacher = current
    let timeout: ReturnType<typeof setTimeout> | undefined
    const renderer = new TeacherAudioRenderer({
      signal: controller.signal,
      getContext: () => this.getTeacherContext(),
      replaceContext: (context) =>
        this.replaceTeacherContext(context, controller.signal),
      resume: (context) => this.resumeTeacherAudio(context, controller.signal),
      paused: () => current.paused,
      progress: progressCallback,
      started: () => startCallback?.(),
    })
    let ended = false
    const end = () => {
      if (ended) return
      ended = true
      endCallback?.()
    }
    const cancel = () => {
      renderer.stop()
      // Invalidate synchronously before another message can reuse a context
      // whose native suspend/resume may still complete after cancellation.
      this.discardFailedRenderer(renderer)
      end()
    }
    controller.signal.addEventListener('abort', cancel, { once: true })
    const resetTimeout = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        controller.abort(AUDIO_LOAD_ERROR)
      }, 35000)
    }
    progressCallback(0)

    try {
      await renderer.prepare()
      const player = renderer.preparedPlayer
      resetTimeout()
      await Promise.race([
        this.edenaiApi.teacherSpeech(
          chatId,
          messageId,
          controller.signal,
          (chunk) => {
            if (controller.signal.aborted) return
            resetTimeout()
            player.append(chunk)
          },
        ),
        renderer.finished,
      ])
      clearTimeout(timeout)
      throwIfSpeechCancelled(controller.signal)
      player.finishInput()
      await renderer.finished
      throwIfSpeechCancelled(controller.signal)
    } catch (error) {
      if (
        !controller.signal.aborted ||
        controller.signal.reason === AUDIO_LOAD_ERROR
      ) {
        const playbackError =
          controller.signal.reason === AUDIO_LOAD_ERROR
            ? new Error(AUDIO_LOAD_ERROR)
            : error
        progressCallback(0)
        this.errorNotifier.onError(playbackError)
      }
    } finally {
      clearTimeout(timeout)
      controller.abort()
      renderer.stop()
      this.discardFailedRenderer(renderer)
      end()
      if (this.currentTeacher === current) this.currentTeacher = undefined
    }
  }

  async playText(
    messageId: number | undefined,
    chatId: number | undefined,
    text: string,
    progressCallback: (progress: number) => void,
    startCallback?: () => void,
    endCallback?: () => void,
  ) {
    if (!this.inited) return
    this.cancelTeacherSpeech()
    const request = ++this.playRequest
    const cleanText = clearTextForSpeech(text)
    progressCallback(0)
    try {
      this.streamService.usePlaybackSession()
      const current = this.currentSound
      if (
        current?.text === cleanText &&
        current.chatId === chatId &&
        current.messageId === messageId
      ) {
        current.controller.abort()
        current.controller = new AbortController()
        current.howl.stop()
        current.howl.seek(0)
        await howlerPlay(
          current.howl,
          progressCallback,
          startCallback,
          endCallback,
          current.controller.signal,
        )
        return
      }
      this.clearSound()
      const url = await this.edenaiApi.textToSpeech(chatId, cleanText)
      // A late teacher response must not replace a word the student just selected.
      if (request !== this.playRequest) {
        URL.revokeObjectURL(url)
        endCallback?.()
        return
      }
      await this.playSource(
        messageId,
        url,
        progressCallback,
        startCallback,
        endCallback,
        {
          text: cleanText,
          chatId,
        },
      )
    } catch (error) {
      if (request === this.playRequest) {
        if (error instanceof Error && error.message === AUDIO_LOAD_ERROR)
          this.clearSound()
        this.errorNotifier.onError(error)
        endCallback?.()
      }
    }
  }

  currentSound?: {
    howl: Howl
    src: string
    controller: AbortController
    messageId: number | undefined
    text?: string
    chatId?: number
  }

  isPlaying() {
    return (
      !!this.currentTeacher ||
      (this.currentSound && this.currentSound.howl.playing())
    )
  }

  setPause(messageId: number, pause: boolean) {
    if (this.currentTeacher?.messageId === messageId && this.teacherContext) {
      this.currentTeacher.paused = pause
      const operation = pause
        ? this.teacherContext.suspend()
        : this.teacherContext.resume()
      void operation.catch((error: unknown) => {
        this.errorNotifier.onError(error)
      })
      return
    }
    if (!this.currentSound || this.currentSound.messageId != messageId) {
      return
    }
    if (pause) {
      this.currentSound.howl.pause()
    } else if (
      !this.currentSound.howl.playing() &&
      this.currentSound.howl.seek() > 0
    ) {
      this.currentSound.howl.play()
    }
  }

  async play(
    messageId: number | undefined,
    src: string,
    progressCallback: (progress: number, duration: number) => void,
    startCallback?: () => void,
    endCallback?: () => void,
  ) {
    if (!this.inited) return
    this.stopCurrentSound()
    const request = this.playRequest
    try {
      await this.playSource(
        messageId,
        src,
        progressCallback,
        startCallback,
        endCallback,
      )
    } catch (error) {
      if (request === this.playRequest) {
        if (error instanceof Error && error.message === AUDIO_LOAD_ERROR)
          this.clearSound()
        this.errorNotifier.onError(error)
        endCallback?.()
      }
    }
  }

  private async playSource(
    messageId: number | undefined,
    src: string,
    progressCallback: (progress: number, duration: number) => void,
    startCallback?: () => void,
    endCallback?: () => void,
    speech?: { text: string; chatId: number | undefined },
  ) {
    this.streamService.usePlaybackSession()
    const howl = new Howl({
      src: [src],
      // Generated WAV URLs carry no extension. Let Howler detect static files such as MP3.
      format:
        src.startsWith('blob:') || isSpeechDownloadUrl(src)
          ? ['wav']
          : undefined,
      html5: true,
      preload: false,
    })
    const controller = new AbortController()
    this.currentSound = { howl, src, messageId, controller, ...speech }
    await howlerPlay(
      howl,
      progressCallback,
      startCallback,
      endCallback,
      controller.signal,
    )
  }

  private clearSound() {
    const current = this.currentSound
    this.currentSound = undefined
    if (current) {
      // Howler queues stop() while loading; explicitly finish the pending promise.
      current.controller.abort()
      current.howl.stop()
      current.howl.unload()
      if (current.src.startsWith('blob:')) {
        URL.revokeObjectURL(current.src)
      }
    }
  }

  stopCurrentSound() {
    this.playRequest++
    this.cancelTeacherSpeech()
    this.clearSound()
  }

  private cancelTeacherSpeech() {
    const current = this.currentTeacher
    this.currentTeacher = undefined
    current?.controller.abort()
  }
}
